import { createContext, useContext } from 'react';
import type { LoginOutcome, SessionState } from '@/services/authClient';

export interface AuthProfileOrigin {
  tenantId?: string;
  userId?: string;
  permissions: string;
  sessionOrigin: string | null;
}

export interface AuthContextValue extends SessionState {
  isLoading: boolean;
  /**
   * Resolves to a signed-in session, or to a workspace choice when the address
   * opens more than one. Callers must narrow with `isWorkspaceSelection`.
   */
  signIn: (input: {
    email: string;
    password: string;
    tenantSlug?: string;
  }) => Promise<LoginOutcome>;
  signInDemo: () => Promise<SessionState>;
  /** The workspace is derived from the address; no tenant slug required. */
  signInWithPasskey: (email: string) => Promise<SessionState>;
  enrollPasskey: () => Promise<{ registered: boolean; message: string | null }>;
  signOut: () => Promise<void>;
  reloadSession: () => Promise<SessionState>;
  applyOwnProfile: (origin: AuthProfileOrigin, profile: {
    id: string; name: string; title: string | null; version: number;
  }) => boolean;
  completePasswordChange: (input: { newPassword: string; currentPassword?: string }) => Promise<SessionState>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) {
    throw new Error('useAuth must be used inside AuthProvider');
  }
  return value;
}
